import React, { useEffect, useState } from "react";
import { Container, Button, Form, Badge } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import { GridColDef } from "@mui/x-data-grid";
import SystemLogTable from "../components/SystemLogTable";
import ViewDetailsModal from "../components/ViewDetailsModal";
import { getListaSystemLog } from "../../../services/systemLogService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import { dataHoraDeUtc } from "../../../utils";
import ExportExcelButton from "../../../components/ExportExcelButton";
import { getPageName } from "../../../locales/translationUtils";
import { useTranslation } from "react-i18next";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { ApiResponse } from "../../../services/api";
import SystemLogModel from "../../../types/systemLog";

const corPorNivel: Record<string, string> = {
  ERRO: "danger",
  DEBUG: "secondary",
  SUCESSO: "success",
};

const SystemLogListView: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<SystemLogModel[]>([]);
  const [apiResponse, setApiResponse] = useState<ApiResponse<SystemLogModel> | null>(null);
  const [exportando, setExportando] = useState(false);
  const [logSelecionado, setLogSelecionado] = useState<SystemLogModel | null>(null);

  // Vazio = todos os niveis.
  const [nivel, setNivel] = useState("");

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });

  const buscar = async () => {
    const resposta = await getListaSystemLog({
      nivel,
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
    });
    setApiResponse(resposta);
    setLogs(resposta?.data?.values ?? []);
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, nivel]);

  const handleExportarExcel = async () => {
    setExportando(true);

    try {
      const resposta = await getListaSystemLog({
        nivel,
        pageNum: '0',
        quantityPerPage: '100000',
      });

      const linhas = (resposta?.data?.values ?? []).map((row) => ({
        [t('systemLog.fields.criadoEm')]: dataHoraDeUtc(row.criadoEm),
        [t('systemLog.fields.nivel')]: row.nivel,
        [t('systemLog.fields.origem')]: row.origem,
        [t('systemLog.fields.mensagem')]: row.mensagem,
        [t('systemLog.fields.excecao')]: row.excecao ?? '',
      }));

      exportarParaExcel(linhas, 'logs_do_sistema');
    } finally {
      setExportando(false);
    }
  };

  const navItems: NavBarItem[] = [
    { label: t('systemLog.labels.home'), route: "/", iconName: "house-door" },
  ];

  const columns: GridColDef[] = [
    {
      field: "criadoEm",
      headerName: t('systemLog.fields.criadoEm'),
      width: 160,
      valueFormatter: (value: any) => dataHoraDeUtc(value),
    },
    {
      field: "nivel",
      headerName: t('systemLog.fields.nivel'),
      width: 110,
      renderCell: (params) => (
        <Badge bg={corPorNivel[params.row.nivel] ?? "secondary"}>{params.row.nivel}</Badge>
      ),
    },
    { field: "origem", headerName: t('systemLog.fields.origem'), width: 220 },
    { field: "mensagem", headerName: t('systemLog.fields.mensagem'), width: 480 },
    {
      field: "ver",
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="link"
          size="sm"
          onClick={() => setLogSelecionado(params.row)}
          title={t('systemLog.labels.viewDetails')}
        >
          <FaEye />
        </Button>
      ),
    },
  ];

  return (
    <Container
      fluid
      style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}
    >
      <NavBar items={navItems} />
      <div className="mb-2"></div>
      <h2>{getPageName("systemLog")}</h2>
      <div className="mb-2"></div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Select
          style={{ width: 220 }}
          value={nivel}
          onChange={(e) => setNivel(e.target.value)}
        >
          <option value="">{t('systemLog.labels.todosNiveis')}</option>
          <option value="ERRO">ERRO</option>
          <option value="DEBUG">DEBUG</option>
          <option value="SUCESSO">SUCESSO</option>
        </Form.Select>

        <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <SystemLogTable
          rows={logs}
          columns={columns}
          apiResponse={apiResponse}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      </div>

      <ViewDetailsModal log={logSelecionado} onClose={() => setLogSelecionado(null)} />
    </Container>
  );
};

export default SystemLogListView;
